<script>
import Vue from 'vue';
import { Flatten, ObjToArray } from '@/lib/utils';

export default {
	name : 'phantom-api-table',

	props : {
		getAll : {
			type : Function,
		},
		get : {
			type : Function,
		},
		search : {
			type : Function,
		},
		setupParams : {
			type : Function
		},
		columns : {
			type     : Object,
			required : true,
		},
		updated : {
			type : Object,
		},
		deleted : {
			type : Object,
		},
	},

	render() {

		return this.$scopedSlots.default( {

			// Data
			rows    : this.rows,
			state   : this.state,
			filters : this.filters,
			length  : this.length,

			// Computed
			isFiltered   : this.isFiltered,
			filteredRows : this.filteredRows,

			// Methods
			fetch           : this.fetch,
			sort            : this.sort,
			search          : this.callSearch,
			updateOffset    : this.updateOffset,
			updateIncrement : this.updateIncrement,
			addFilter       : this.addFilter,
			clearFilter     : this.clearFilter,
		} );

	},

	data : () => ( {
		rows   : [],
		length : 0,
		state  : 'init',

		// The offset and increment used on the last fetch
		offset    : 0,
		increment : 15,

		filters : {},

		sortedColumn : {
			key       : '',
			direction : 0
		},

		searchResults : [],

	} ),

	computed : {
		isFiltered() {
			const { filters } = this;

			let isFiltered = false;

			Object.keys( filters ).forEach( ( key ) => {

				const filter = filters[key];

				if ( typeof filter === 'object' && !Array.isArray( filter ) ) {

					if ( Object.keys( filter ).length ) {
						isFiltered = true;
					}

					return;
				}

				if ( filter.length ) {
					isFiltered = true;
				}

			} );

			return isFiltered;
		},

		getParams() {
			const { filters, columns, sortedColumn } = this;
			return this.setupParams( filters, columns, sortedColumn );
		},

		cols() {
			const cols = ObjToArray( this.columns );

			return cols.sort( ( a, b ) => a.order - b.order );
		}

	},

	created() {
		this.init();
	},

	watch : {

		updated : {
			immediate : true,
			deep      : true,
			handler( newVal ) {

				if ( !newVal ) {
					return;
				}

				const { asset : assetName } = newVal;
				const foundIndex = this.rows.findIndex( row => parseInt( row[`${assetName}Key`], 10 ) === parseInt( newVal.key, 10 ) );

				if ( foundIndex === -1 ) {

					this.get( newVal.key )
						.then( Flatten )
						.then( ( fullAsset ) => {

							// eslint-disable-next-line no-unused-vars
							const lastRowIndex = this.rows.length - 1;
							this.rows.unshift( fullAsset );

							// Vue.set( this.rows, lastRowIndex + 1, fullAsset );

						} );

					return;
				}

				const setValue = ( data ) => {
					// if the value already exists in our table we'll be able to
					// use it's index later

					// in the event that the value already exists in our table
					// just update it
					const row  = this.rows[foundIndex];
					const keys = Object.keys( row );

					keys.forEach( ( key ) => {
						Vue.set( this.rows[foundIndex], key, data[key] );
					} );

					this.$emit( 'updated', data );
				};

				this.get( newVal.key )
					.then( Flatten )
					.then( setValue );
			}
		},

		deleted : {
			immediate : true,
			deep      : true,
			handler( deletedVal ) {

				// console.log( deletedVal );

				if ( !deletedVal ) {
					return;
				}

				const { asset : assetName } = deletedVal;
				const foundIndex = this.rows.findIndex( row => parseInt( row[`${assetName}Key`], 10 ) === parseInt( deletedVal.key, 10 ) );

				// if for some reason the row doesn't exist,
				// don't continue
				if ( foundIndex === -1 ) {
					return;
				}

				this.rows.splice( foundIndex, 1 );

				this.$emit( 'deleted' );

			}
		},

		cols( cols ) {

			if ( !cols.length ) {
				return;
			}

			this.setFirstSortedColumn();

		},

		getParams() {
			this.$emit( 'get-params', this.getParams );
			this.fetch( this.offset, this.increment );
		},
	},

	methods : {

		setFirstSortedColumn() {
			const { cols } = this;
			const firstColumn = cols.find( col => col.hidden === false );
			const { filterColumn } = this.columns[firstColumn.key];
			this.sortedColumn = {
				key       : filterColumn || firstColumn.key,
				direction : -1
			};
		},

		init() {
			this.setFirstSortedColumn();
			this.state = 'idle';
		},

		fetch( offset, increment ) {
			const { getParams } = this;

			if ( this.state === 'search' ) {
				this.rows   = this.searchResults.slice( offset, offset + increment );
				this.length = this.searchResults.length;
				return;
			}

			this.state = 'loading';

			// ! Don't remove. This is necessary to prevent
			// ! memory leaks
			this.rows.forEach( ( row, i ) => {
				delete this.rows[i];
			} );

			this.rows = [];

			this.getAll( offset, increment, getParams )
				.then( ( { results, total } ) => {
					// console.log( results );
					this.rows   = results.map( result => Flatten( result ) );
					this.length = total;
					this.state  = 'idle';
				} )
				.catch( ( error ) => {
					console.warn( error );

					this.rows   = [];
					this.length = 0;
					this.state  = 'failed';
				} );

		},

		sort( type, key, direction ) {
			const { filterColumn } = this.columns[key];

			this.sortedColumn.key       = filterColumn || key;
			this.sortedColumn.direction = direction;
		},

		callSearch( query ) {
			// Setting this to search will set the page to 1 PaginatedTableView
			// This will trigger a change in offset and emit fetch
			this.state = 'loading';

			if ( !query ) {
				this.searchResults = [];
				this.state         = 'idle';
				// PaginatedTableView emits a fetch when the page changes. If the page is at 1
				// and you search, it wont emit a fetch so PhantomApiTable wont know to fetch new data
				if ( this.offset === 0 ) {
					this.fetch( this.offset, this.increment );
				}

				return;
			}

			this.search( query, this.getParams )
				.then( ( results ) => {
					this.state = 'search';
					this.searchResults = results;

					if ( this.offset === 0 ) {
						this.fetch( this.offset, this.increment );
					}

				} );

		},

		updateOffset( offset ) {
			this.offset = offset;
		},

		updateIncrement( increment ) {
			this.increment = increment;
		},

		addFilter( col, criterion ) {
			const { filterColumn, key } = col;
			if ( criterion === '' || criterion === null ) {
				Vue.delete( this.filters, filterColumn || key );

				return;
			}

			Vue.set( this.filters, filterColumn || key, criterion );
		},

		clearFilter() {
			this.filters = {};
		},

	}
};
</script>
